import { useContext, useEffect } from 'react';
import { MarkerLayerContext } from './marker-layer';

import iconFilledBlueMarker from './assets/filled-blue-marker.png';
import iconFilledRedMarker from './assets/filled-red-marker.png';
import iconFilledGreyMarker from './assets/filled-grey-marker.png';

import iconBigGreyMarker from './assets/bigGreyMarker.png';
import iconBigRedMarker from './assets/bigRedMarker.png';

import { MarkerProps, MarkerType, CrzpMarker, MarkerSize } from './map-types';
import { useEventCallback } from '@eas/common-web';
import { noop } from 'lodash';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const mapMarkerTypeToIcon: Record<MarkerType, string> = {
  BIG_GREY: iconBigGreyMarker,
  BIG_RED: iconBigRedMarker,
  DEFAULT: iconBigRedMarker,
  FILLED_BLUE: iconFilledBlueMarker,
  FILLED_GREY: iconFilledGreyMarker,
  FILLED_RED: iconFilledRedMarker,
};

const mapMarkerSize: Record<MarkerSize, { width: number; height: number }> = {
  small: { width: 25, height: 33 },
  medium: { width: 35, height: 46 },
  large: { width: 50, height: 66 },
};

// Helper function to create L.DivIcon for Leaflet
function createLeafletDivIcon(
  iconAsset: string,
  options: {
    width?: number;
    height?: number;
    innerText?: string;
    textColor?: string;
    textTopOffset?: string;
  } = {}
): L.DivIcon {
  const width = options.width || 32; // Default width if not specified
  const height = options.height || 32; // Default height if not specified
  const innerText = options.innerText;
  const textColor = options.textColor || 'black';
  const textTopOffset = options.textTopOffset || '13px'; // Default for larger markers

  let htmlContent = `
    <div style="width:${width}px; height:${height}px; position:relative; display:flex; justify-content:center; align-items:flex-end;">
      <img src="${iconAsset}" style="width:100%; height:100%;" alt="marker"/>
  `;
  if (innerText) {
    htmlContent += `
      <div style="position:absolute; width:100%; top:${textTopOffset}; text-align:center; font-weight:bold; color: ${textColor}; font-size: 10px; pointer-events: none;">
        ${innerText}
      </div>
    `;
  }
  htmlContent += `</div>`;

  return L.divIcon({
    html: htmlContent,
    className: '',
    iconSize: [width, height],
    // Anchor at the bottom-center point of the div
    iconAnchor: [width / 2, height],
    // Adjust popup anchor relative to iconAnchor
    popupAnchor: [0, -height + 5],
  });
}

export const createMarkerIcon = (
  type: MarkerType,
  size: MarkerSize = 'small',
  innerText?: string
) => {
  return createLeafletDivIcon(mapMarkerTypeToIcon[type], {
    ...mapMarkerSize[size],
    innerText,
  });
};

const selectedPinIcon = createMarkerIcon(MarkerType.FILLED_BLUE, 'small');
const inactivePinIcon = createMarkerIcon(MarkerType.FILLED_GREY, 'small');

export function createLeafletMarker(
  markerData: MarkerProps
): { leafletMarker: L.Marker; markerData: CrzpMarker } {
  const {
    lat,
    lon,
    innerTitle,
    type = MarkerType.DEFAULT,
    id,
    active,
    selected,
    draggable,
    size,
    customIcon,
    markerLeafletOptions,
  } = markerData;

  let markerIcon: L.Icon | L.DivIcon | undefined = undefined;
  let markerTitleText = draggable ? 'Přesunte pomocí myši' : undefined;

  if (customIcon) {
    markerIcon = customIcon;
  } else {
    if (selected) {
      markerIcon = selectedPinIcon;
    } else if (active === false) {
      markerIcon = inactivePinIcon;
      markerTitleText = 'Neaktivní';
    } else {
      markerIcon = createMarkerIcon(type, size, innerTitle);
    }
  }

  const leafletOptions: L.MarkerOptions = {
    draggable,
    title: markerTitleText,
    ...markerLeafletOptions,
  };

  if (markerIcon) {
    leafletOptions.icon = markerIcon;
  }

  const leafletMarker = L.marker([Number(lat), Number(lon)], leafletOptions);

  if (id !== undefined) {
    (leafletMarker as any).markerId = id;
  }

  return {
    leafletMarker,
    markerData,
  };
}

export function Marker({
  id,
  lon,
  lat,
  onDragEnd = noop,
  draggable = false,
  ...rest
}: MarkerProps) {
  const markerLayer = useContext(MarkerLayerContext);

  const handleDragStart = useEventCallback((e) => {
    if (draggable) {
      const markerElement = (e.target as L.Marker).getElement();
      if (markerElement) {
        markerElement.style.cursor = 'grabbing';
      }
    }
  });
  const handleDragStop = useEventCallback((e) => {
    if (draggable) {
      const markerElement = (e.target as L.Marker).getElement();
      if (markerElement) {
        markerElement.style.cursor = ''; // Or 'grab' if it's still draggable
      }
      const latLng = (e.target as L.Marker).getLatLng();
      onDragEnd({ lon: latLng.lng, lat: latLng.lat });
    }
  });
  useEffect(() => {
    if (!lat || !lon || !markerLayer) {
      return;
    }

    const { leafletMarker } = createLeafletMarker({
      lat,
      lon,
      id,
      draggable,
      ...rest,
    });

    if (draggable) {
      leafletMarker.on('dragstart', handleDragStart);
      leafletMarker.on('dragend', handleDragStop);
    }

    leafletMarker.addTo(markerLayer);

    return () => {
      if (draggable) {
        leafletMarker.off('dragstart', handleDragStart);
        leafletMarker.off('dragend', handleDragStop);
      }
      leafletMarker.remove(); // Remove the marker from the map
    };
  }, [
    lat,
    lon,
    markerLayer,
    id,
    draggable,
    handleDragStart,
    handleDragStop,
    rest.type,
    rest.innerTitle,
    rest.active,
    rest.selected,
    rest,
  ]);

  return null;
}
